import { memo, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import SeverityDropdown from "../../../SeverityDropdown";

import {
  Finding,
  useFindingStore,
  FindingSeverityLabel,
  FindingSeverityColor,
  FindingSeverityNameToIntMap,
} from "stores/FindingStore";

import { notifyError, notifySuccess } from "utility/notify";

// -----------------------------------------------------------------------------

const FindingEditModal = memo(function FindingEditModal({
  finding,
  onClose,
}: FindingEditModalProps): JSX.Element | null {
  const [findingStore, actions] = useFindingStore();
  const { findingTypes } = findingStore;

  //
  const [override, setOverride] = useState(false);
  const [newSeverity, setNewSeverity] = useState(
    FindingSeverityLabel[finding.severity]
  );
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  //
  const defaultSeverity = findingTypes[finding.name]?.default_severity;
  const startingSeverity = FindingSeverityLabel[finding.severity];

  //
  const onSave = () => {
    setLoading(true);

    // If the user selects the same severity, we don't want to do anything
    if (newSeverity === startingSeverity && !override) {
      setErrorMessage(
        `Please choose a severity that differs from the current severity of "${startingSeverity}"`
      );
      setLoading(false);
      return;
    }

    // If the user wants to bulk edit, we call the setOrgSeverityOverride action
    // Otherwise, we call the updateSeverity action
    if (override) {
      actions.setOrgSeverityOverride({
        updatedFinding: finding,
        newSeverity: FindingSeverityNameToIntMap[newSeverity],
        onSuccess: onSaveSuccess,
        onError: onSaveError,
      });
    } else {
      actions.updateSeverity({
        uuid: finding.uuid,
        newSeverity: FindingSeverityNameToIntMap[newSeverity],
        onSuccess: onSaveSuccess,
        onError: onSaveError,
      });
    }
  };

  const onSaveSuccess = () => {
    notifySuccess(
      `Successfully set the severity of the selected finding to ${newSeverity}`
    );
    setLoading(false);
    onClose();
  };

  const onSaveError = () => {
    notifyError(
      "Something went wrong trying to edit the severity of the selected finding"
    );
    setLoading(false);
  };

  //
  return (
    <Dialog open={true} onClose={onClose} fullWidth>
      <DialogTitle>Edit Severity</DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: "center" }}>
          <TextField
            label="Default Severity"
            variant="outlined"
            disabled
            InputProps={{
              startAdornment: (
                <Chip
                  key={FindingSeverityLabel[defaultSeverity ?? -1] || ""}
                  label={FindingSeverityLabel[defaultSeverity ?? -1] || ""}
                  color={FindingSeverityColor[defaultSeverity ?? 0] as any}
                  sx={{ fontWeight: "bold", mt: 2, mb: 2 }}
                />
              ),
            }}
            sx={{ flex: 1 }}
          />
          <Typography variant="body1">to</Typography>
          <Box sx={{ flex: 1 }}>
            <SeverityDropdown
              severity={newSeverity}
              onChange={setNewSeverity}
            />
          </Box>
        </Stack>
        {errorMessage && (
          <Typography variant="body1" mt={1} color="error">
            {errorMessage}
          </Typography>
        )}

        <FormGroup sx={{ mt: 2 }}>
          <FormControlLabel
            label={
              <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                Apply this override to all findings of type:{" "}
                <strong>{finding.name}</strong> which do not already have an
                individual severity override configured
              </Typography>
            }
            control={
              <Checkbox
                onChange={(event) => {
                  setOverride(event.target.checked);
                }}
              />
            }
          />
        </FormGroup>
      </DialogContent>

      <DialogActions sx={{ pt: 0, pb: 3, px: 3 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton loading={loading} variant="contained" onClick={onSave}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
});

export default FindingEditModal;

// -----------------------------------------------------------------------------

interface FindingEditModalProps {
  finding: Finding;
  onClose: () => void;
}
